import React, { useEffect, useMemo, useState } from 'react'
import styles from './styles.module.scss'
import ItemInfos from '@/src/interfaces/catalog/item-infos'
import { SelectAnswerProps, useCurrentProduct } from '@/src/context/product/current-product-context'
import BottomSheet from '@/src/components/helpers/overlays/bottom-sheet/bottom-sheet'
import ModalAddCustom from '@/src/components/product/modal-add-custom/modal-add-custom'
import AnswerTitleComponent from './answer-title/answer-title'
import { AnswerType } from '@/src/hooks/product/useWizard'
import AnswerTypeComponent from './answer-type/answer-type'
import AnswerPriceComponent from './answer-price/answer-price'

interface Props {
    itemOption: ItemInfos.ItemOption
    itemGroup: ItemInfos.ItemGroup
}

export default function Answer(props: Props) {
    const { handleSelectedAnswer, answersFromCustomer } = useCurrentProduct()

    const [quantity, setQuantity] = useState<number>(0)
    const [disableOption, setDisableOption] = useState<boolean>(false)
    const [bottomSheet, setBottomSheet] = useState<boolean>(false)
    const [isSelected, setIsSelected] = useState<boolean>(false)
    const [isGroupFullyAnswered, setIsGroupFullyAnswered] = useState<boolean>(false)

    const itemOption = props.itemOption
    const itemGroup = props.itemGroup
    const isCombo = itemGroup.groupingType === 'Combo'

    const answerType: AnswerType = useMemo(() => {
        const isObservation = !itemOption.itemChild || itemGroup.itemOptionsGroup.every(i => i.maxQty === 1)
        const canJustOneAnswer = itemGroup.maxQtyAnswer === 1 && itemGroup.isRequired
        const canJustOneObservation = itemGroup.maxQtyItems === 1 || isObservation

        if ((canJustOneAnswer && canJustOneObservation) || isCombo)
            return 'radio'
        else if (canJustOneObservation)
            return 'checkbox'
        else
            return 'add'
    }, [itemGroup, itemOption])

    const handleCustomValueConfirm = (customValue: number) => {
        handleUpdateQuantity(customValue)
        setBottomSheet(false)
    }

    const handleUpdateQuantity = (newQuantity: number) => {
        setQuantity(newQuantity)

        const answerProps: SelectAnswerProps = {
            itemOption: itemOption,
            itemGroup: itemGroup,
            isCombo: false,
            mustUpdatePrice: true,
            quantity: newQuantity,
            answerType: answerType
        }

        handleSelectedAnswer(answerProps)
    }

    const handleClick = () => {
        let quantityItem = itemOption.quantity > 1 ? itemOption.quantity : quantity

        const answerProps: SelectAnswerProps = {
            itemOption: itemOption,
            itemGroup: itemGroup,
            isCombo: isCombo,
            mustUpdatePrice: false,
            quantity: quantityItem || 1,
            answerType: answerType
        }

        if (isSelected && answerType !== 'radio') {
            // Deseleciona o item se já estiver selecionado
            answerProps.quantity = 0
        }

        // Seleciona o item se o máximo de respostas ainda não foi atingido
        handleSelectedAnswer(answerProps)
    }

    const sumOfQtyAnswered = () => {
        if(!itemGroup.isRequired)
            return itemOption.quantity

        const currentAnswers = answersFromCustomer.filter((c) => c.id === itemGroup.id)
        const sumOfQtyAnswered = currentAnswers.reduce((a, b) => {
            const qty = b.itemOptionsGroup.reduce((x, y) => x + y.quantity, 0)
            return a + qty
        }, 0)

        return sumOfQtyAnswered
    }

    useEffect(() => {
        const isDisable = isGroupFullyAnswered && !isSelected
        setDisableOption(isDisable)
    }, [isGroupFullyAnswered, isSelected])

    useEffect(() => {
        if (answerType === 'radio') {
            setIsGroupFullyAnswered(false)
        }
        else {
            // Soma total das quantidades respondidas para o grupo
            const sum = sumOfQtyAnswered()

            // Quantidade de opções selecionadas
            const selectedCount = answersFromCustomer?.find((c) => c.id === itemGroup.id)?.itemOptionsGroup?.length || 0
            const validateSumQty = sum >= itemGroup.maxQtyItems

            // Se o grupo exige soma exata (ex: 100 itens)
            if (itemGroup.maxQtyItems && itemGroup.maxQtyItems > 1) {
                // Só está completo se a soma for exatamente igual ao máximo
                const value = sum === itemGroup.maxQtyItems

                if (selectedCount >= itemGroup.maxQtyAnswer)
                    setIsGroupFullyAnswered(true)
                else
                    setIsGroupFullyAnswered(value)
            }
            // Se o grupo exige quantidade de opções (ex: 2 a 4 opções)
            else if (itemGroup.minQtyAnswer && itemGroup.maxQtyAnswer) {
                let value = selectedCount >= itemGroup.minQtyAnswer && selectedCount <= itemGroup.maxQtyAnswer
                if (selectedCount <= itemGroup.maxQtyAnswer)
                    setIsGroupFullyAnswered(false)
                else
                    setIsGroupFullyAnswered(value)
            }
            // Para checkbox simples
            else if (itemGroup.isRequired) {
                const value = selectedCount > 0
                setIsGroupFullyAnswered(value)
            }
            else {
                // Se pode adicionar mais itens
                const value = selectedCount >= itemGroup.maxQtyAnswer || validateSumQty
                setIsGroupFullyAnswered(value)
            }
        }
    }, [answerType, answersFromCustomer])

    useEffect(() => {
        if (!answersFromCustomer)
            return

        const currentAnswer = answersFromCustomer.find((c) => c.id === itemGroup.id)
        const select = !!currentAnswer?.itemOptionsGroup?.find((c) => c.id === itemOption.id)

        setIsSelected(select)
    }, [answersFromCustomer])

    useEffect(() => {
        const isSingleQuestion = itemGroup.itemOptionsGroup && itemGroup.itemOptionsGroup.length === 1
        const question = document.getElementById(itemOption.id)

        if (isSingleQuestion && itemGroup.isRequired && question) {
            setTimeout(() => {
                question.click()
                setIsSelected(true)
            }, 10)
        }
    }, [answerType])

    return (
        <>
            {bottomSheet && (
                <BottomSheet isOpen={bottomSheet} onClose={() => setBottomSheet(false)}>
                    <ModalAddCustom
                        onConfirm={handleCustomValueConfirm}
                        initialValue={quantity}
                        wizard
                        maxValue={itemOption.maxQty}
                        sumOfQtyAnswered={sumOfQtyAnswered()}
                        itemGroup={itemGroup}
                    />
                </BottomSheet>
            )}
            <div
                id={itemOption.id}
                className={`${styles.answer} ${disableOption ? styles.disable : ''}`}
                onClick={() => answerType !== 'add' && handleClick()}
            >
                <AnswerTitleComponent
                    isCombo={isCombo}
                    answerType={answerType}
                    isSelected={isSelected}
                    itemOption={itemOption}
                />
                <AnswerPriceComponent
                    isCombo={isCombo}
                    itemOption={itemOption}
                    quantity={quantity}
                />
                <AnswerTypeComponent
                    answerType={answerType}
                    itemOption={itemOption}
                    itemGroup={itemGroup}
                    quantity={quantity}
                    isSelected={isSelected}
                    handleUpdateQuantity={(e) => handleUpdateQuantity(e)}
                    setBottomSheet={(e) => setBottomSheet(e)}
                    sumOfQtyAnswered={sumOfQtyAnswered()}
                />
            </div>
        </>
    )
}
